import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import {
    Button,
    Grid,
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    TableContainer,
    TableRow,
    Table,
    TableBody,
    TableCell,
    Tooltip,
    TableHead,
    FormControlLabel,
    Checkbox,
    Modal,
    Typography,
    FormHelperText,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    RadioGroup,
    Radio,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import DangerousIcon from '@mui/icons-material/Dangerous';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    getCPTCodesForBHDiagnostic,
    getFCodesForBHDiagnostic,
    gotoPreviousMenu,
    setAssessmentError,
    setBaseCode,
    setFCodeCategory,
    setFirstLevelOptions,
    setFirstLevelSelection,
    setSecondLevelOptions,
    setSecondLevelSelection,
    setShowFCodeModal,
    setThirdLevelOptions,
    setThirdLevelSelection,
} from '../../../store/assessment.slice';
import { RootState } from '../../../reducers';
import {
    getMeansOfEngagements,
    setReasonForVisit,
    saveEngagementDraft,
    setEngagementDraft,
    setEngagementContacted,
    toggleShowSummaryNoteModal,
    checkIsOnboardedAndGetReason,
    getReasonForVisit,
    fetchLastYearlyBHDiagnostic,
} from '../../../store/documentEngagement.slice';
import { ReasonForVisit } from '../../../Models/DocumentEngagements/EngagementDraft.model';
import { DocumentEngagement } from '../../../Models/DocumentEngagements/DocumentEngagement.model';
import classes from '../Styles';
import { getBHDiagnosticAssessmentsList, phoneOptions, roleEnum, summaryNoteType, summaryNoteTypeList } from '../../../utils/assessments';
import { SummaryNoteTypeEnum } from '../../../Enum/SummaryNoteTypeEnum';
import { copyObject } from '../../../utils/common';
import { isSummaryNoteConfirmationDisabled } from '../../../utils/summaryNoteConfirmation';
import { MeansOfEngagementEnum } from '../../../Enum/MeansOfEngagementEnum';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { isValid, isBefore, subDays } from 'date-fns';
import { toDate } from '../../../utils/dateHelpers';
import AlertDialog from '../../AlertDialog/AlertDialog';
import DSM5 from '../../../assets/documents/DSM_5.pdf';
import { DiagnosticType } from '../../../Enum/DiagnosticType';

import FCodeModal from './FCodeModal';
import { MenuDetails } from '../../../Models/Assessment/AssessmentMenu.model';
import { CPTcode } from '../../../Models/Shared/CPTCodeGroup';

import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Summary_Note.pdf';

const summaryNoteFormSchema = Yup.object().shape({
    MeansOfEngagement: Yup.string()
        .required('Select means of engagement.')
        .test('MeansOfEngagement', 'Select means of engagement.', (value) => {
            if (Boolean(value) && value !== '0' && parseInt(value) !== 0) {
                return Boolean(value);
            } else return false;
        }),
    ReasonForVisit: Yup.object()
        .nullable()
        .shape({
            Id: Yup.string().nullable().required('Select reason.'),
            Name: Yup.string(),
        })
        .required('Select reason.'),
    VisitedTime: Yup.string().nullable().required('Select visited time.'),
    Contacted: Yup.boolean().nullable().required('Select contacted.'),
});

const statementOfCareOptions = ['Yes - Member is engaged in resources', 'No - Member declined resources'];

const SummaryNotes = () => {
    const YEARLY_CPT_CODE = '90791';
    const [newNote, onChangeNote] = useState('');
    const dispatch = useAppDispatch();

    const newNoteEditor = useEditor({
        extensions: [StarterKit],
        content: newNote,
        onUpdate: ({ editor }) => {
            onChangeNote(editor.getHTML());
        },
    });

    useEffect(() => {
        if (newNoteEditor && newNote !== newNoteEditor.getHTML()) {
            newNoteEditor.commands.setContent(newNote || '');
        }
    }, [newNote]);
    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const { assessmentMenuList, bhCPTCodeGroup, selectedRole, miniMentalExam } = useSelector((state: RootState) => state.assessment);
    const { memberBasicInfo } = useSelector((state: RootState) => state.memberDetail);
    const {
        meansOfEngagements,
        reasonForVisits,
        engagementDraft,
        memberAssessmentHistory,
        showSummaryNoteModal,
        isSaving,
        isFetching,
        lastYearlyBHDiagnostic,
    } = useSelector((state: RootState) => state.documentEngagement);
    const [isMemberOnboarded, setIsMemberOnboarded] = useState<boolean>(false);
    const [lastReasonForVisit, setLastReasonForVisit] = useState<string>();
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [cptCodeError, setCptCodeError] = useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const isInHra = selectedRole === roleEnum.HRA;
    const isInFuhfum = memberBasicInfo.IsInFuhFum;
    const isBHDiagnostic = engagementDraft.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;

    // Get the dynamic list of required BH Diagnostic assessments (conditionally includes Suicidal/Homicidal Ideation)
    const bhDiagnosticAssessmentsList = getBHDiagnosticAssessmentsList(miniMentalExam);

    const diagnosticsCompleted = (assessmentMenuList: MenuDetails[], requiredAssessments: string[]) => {
        let completed = true;
        requiredAssessments.forEach((item) => {
            const bhDiagnosticAssessment = assessmentMenuList.find((assessment) => assessment.Name === item);
            if (!bhDiagnosticAssessment || (bhDiagnosticAssessment && !bhDiagnosticAssessment.IsCompleted)) {
                completed = false;
            }
        });
        return completed;
    };
    const hasCompletedAllBHDiagnosticAssessments = diagnosticsCompleted(assessmentMenuList, bhDiagnosticAssessmentsList);
    const onboardedDesc = 'Is onboarded';
    const transitionalCareDesc = 'Transitional Care';
    const facilityVisitDesc = 'Facility Visit';

    const summaryNotesWithFCodes = engagementDraft.SummaryNotes.filter((s) => Boolean(s.Zcode) && s.Zcode.toLowerCase().startsWith('f'));

    const ConfirmationDialog = (props: any) => {
        const { open, onClose, onConfirm } = props;
        return (
            <Dialog open={open} onClose={onClose}>
                <DialogTitle>Confirm F Code selections</DialogTitle>
                <DialogContent>
                    <Typography>
                        There are{' '}
                        <b style={{ color: 'red', fontSize: '1.25rem' }}>{summaryNotesWithFCodes.filter((s) => s.IsConfirm).length} F codes selected</b> for
                        this BH Diagnostic. Are you sure you wish to continue to procedure sign off or cancel and add more diagnosis?
                    </Typography>
                    <List>
                        {summaryNotesWithFCodes
                            .filter((s) => s.IsConfirm)
                            .map((fcode, index) => (
                                <ListItem key={fcode.Id + '_' + index} divider={index !== summaryNotesWithFCodes.length - 1}>
                                    <ListItemText sx={{ fontStyle: 'italic' }} primary={`${fcode.Note}`} />
                                </ListItem>
                            ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} sx={{ minWidth: 120 }} color="primary" variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            onConfirm();
                            onClose();
                        }}
                        sx={{ minWidth: 120 }}
                        color="primary"
                        variant="contained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const handleOpenAddFcodeModal = () => {
        dispatch(setFCodeCategory(''));
        dispatch(setBaseCode(''));
        dispatch(setFirstLevelSelection(''));
        dispatch(setFirstLevelOptions([]));
        dispatch(setSecondLevelSelection(''));
        dispatch(setSecondLevelOptions([]));
        dispatch(setThirdLevelSelection(''));
        dispatch(setThirdLevelOptions([]));
        dispatch(setShowFCodeModal(true));
    };

    const checkCodeIsDisabled = (cptCodeGroup: CPTcode) => {
        const completedDate = toDate(lastYearlyBHDiagnostic?.CompletedDate);
        const cutoffDate = subDays(new Date(), 365);
        if (lastYearlyBHDiagnostic && completedDate && !isBefore(completedDate, cutoffDate)) {
            if (cptCodeGroup.Code === YEARLY_CPT_CODE) {
                return true;
            } else {
                return false;
            }
        } else {
            if (cptCodeGroup.Code === YEARLY_CPT_CODE) {
                return false;
            } else {
                return true;
            }
        }
    };

    useEffect(() => {
        dispatch(getMeansOfEngagements());
        if (!isInFuhfum) {
            dispatch(
                checkIsOnboardedAndGetReason(engagementDraft.MemberId, engagementDraft.Id, (data: ReasonForVisit[], isOnboared: boolean) => {
                    setIsMemberOnboarded(isOnboared);
                    if (isInHra && !Boolean(engagementDraft.ReasonForVisit)) {
                        const hraReason = data.find((d) => d.Name === roleEnum.HRA);
                        if (Boolean(hraReason)) {
                            dispatch(setEngagementDraft({ ...engagementDraft, ReasonForVisit: hraReason }));
                        }
                    } else if (!isOnboared && !Boolean(engagementDraft.ReasonForVisit)) {
                        const reasonForVisit = data.find((d) => d.Name === onboardedDesc);
                        if (Boolean(reasonForVisit)) {
                            dispatch(setEngagementDraft({ ...engagementDraft, ReasonForVisit: reasonForVisit }));
                        }
                    } else if (
                        (isOnboared && !Boolean(engagementDraft.ReasonForVisit)) ||
                        (isOnboared && Boolean(engagementDraft.ReasonForVisit) && engagementDraft.ReasonForVisit.Name !== onboardedDesc)
                    ) {
                        data = data.filter((r) => r.Name !== onboardedDesc);
                        dispatch(setReasonForVisit(data));
                    }
                })
            );
        } else {
            dispatch(
                getReasonForVisit((reasonForVisits) => {
                    if (!Boolean(engagementDraft.ReasonForVisit)) {
                        if (selectedRole === roleEnum.NURSE_PRACTITIONER || selectedRole === roleEnum.BEHAVIOURAL_HEALTH_SPECIALIST) {
                            const transitionalCareReason = reasonForVisits.find((d) => d.Name === transitionalCareDesc);
                            dispatch(setEngagementDraft({ ...engagementDraft, ReasonForVisit: transitionalCareReason }));
                        } else {
                            const facilityReason = reasonForVisits.find((d) => d.Name === facilityVisitDesc);
                            dispatch(setEngagementDraft({ ...engagementDraft, ReasonForVisit: facilityReason }));
                        }
                    }
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (isFetching && !isPageLoaded) {
            setIsPageLoaded(true);
        }
    }, [isFetching, isPageLoaded]);

    useEffect(() => {
        dispatch(fetchLastYearlyBHDiagnostic(engagementDraft.MemberId));
    }, [engagementDraft.MemberId, dispatch]);

    useEffect(() => {
        dispatch(getCPTCodesForBHDiagnostic());
        dispatch(getFCodesForBHDiagnostic());
    }, [dispatch]);

    const handleSubmitBHDiagnostic = (values: DocumentEngagement) => {
        const el = document.getElementById('diagnostic-results');

        if (!values.DiagnosticStatus?.CPTcode?.Id) {
            setCptCodeError(true);
            dispatch(setAssessmentError({ Show: true, Message: 'CPT code is required for BH Diagnostic engagement.' }));
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        } else if (summaryNotesWithFCodes.filter((s) => s.IsConfirm).length === 0) {
            dispatch(setAssessmentError({ Show: true, Message: 'At least one F-Code is required for BH Diagnostic engagement.' }));
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        } else {
            setShowConfirmationDialog(true);
            return;
        }
    };

    const handleSubmitAssessment = (values: DocumentEngagement) => {
        let data = copyObject(values);
        data.Notes = '';
        values.SummaryNotes.filter((s) => s.IsConfirm).forEach((note) => {
            if (Boolean(note.Note)) {
                data.Notes += `<p> ${summaryNoteTypeList.find((s) => s.value === note.Type)?.name || '-'} : ${note.Note} </p>`;
            }
        });
        const updatedMemberAssessmentStatuses = {
            ...memberAssessmentHistory.MemberAssessmentStatuses,
            DocumentEngagementStatus: StatusEnum.Completed,
        };

        if (data.Contacted !== false && (isBHDiagnostic || hasCompletedAllBHDiagnosticAssessments)) {
            data.DiagnosticStatus.CompletedDate = new Date().toISOString();
            data.DiagnosticStatus.DiagnosticAssessmentStatus = StatusEnum.Completed;
            data.DiagnosticStatus.DiagnosticType = DiagnosticType.BHDiagnostic;
            const summaryNotesCopy = [...data.SummaryNotes];
            summaryNotesCopy.forEach((note) => {
                if (note.Zcode && (note.Zcode.toLowerCase().startsWith('f') || note.Zcode.toLowerCase().startsWith('t'))) {
                    note.CptCode = data.DiagnosticStatus.CPTcode.Code;
                }
            });
            data.SummaryNotes = summaryNotesCopy;

            if (data.DiagnosticStatus.CPTcode?.Code === YEARLY_CPT_CODE) {
                data.DiagnosticStatus.IsOncePerYear = true;
            } else {
                data.DiagnosticStatus.IsOncePerYear = false;
            }
        }
        const updatedMemberAssessmentHistory = {
            ...memberAssessmentHistory,
            EngagementDraft: data,
            MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
        };
        dispatch(saveEngagementDraft(updatedMemberAssessmentHistory, true));
    };

    const handleSubmit = (values: DocumentEngagement) => {
        if (values.Contacted !== false && (isBHDiagnostic || hasCompletedAllBHDiagnosticAssessments)) {
            handleSubmitBHDiagnostic(values);
        } else {
            handleSubmitAssessment(values);
        }
    };

    const changeReasonForVisit = (id: string, values: DocumentEngagement, setFieldValue: Function) => {
        const lastReasonForVisit = values.ReasonForVisit;
        const reasonForVisit = reasonForVisits.find((d) => d.Id === id);
        if (
            !isMemberOnboarded &&
            Boolean(reasonForVisit) &&
            reasonForVisit.Name !== onboardedDesc &&
            Boolean(lastReasonForVisit) &&
            lastReasonForVisit.Name === onboardedDesc
        ) {
            setShowAlert(true);
            setLastReasonForVisit(id);
        } else {
            if (!isMemberOnboarded && Boolean(reasonForVisit) && reasonForVisit.Name !== onboardedDesc) {
                setFieldValue('IsDefaultReasonForVisitChanged', true);
            } else {
                setFieldValue('IsDefaultReasonForVisitChanged', false);
            }
            setFieldValue('ReasonForVisit', reasonForVisit);
        }
    };

    const handleOnConfirm = (values: DocumentEngagement, setFieldValue: Function) => {
        const reasonForVisit = reasonForVisits.find((d) => d.Id === lastReasonForVisit);
        if (Boolean(reasonForVisit)) {
            setFieldValue('ReasonForVisit', reasonForVisit);
            setFieldValue('IsDefaultReasonForVisitChanged', true);
            setShowAlert(false);
        }
    };

    const handleOnAddSummaryNote = (values) => {
        let data = copyObject(values);
        data.SummaryNotes = data.SummaryNotes || [];
        data.SummaryNotes.push({
            Note: newNote,
            Type: SummaryNoteTypeEnum.Custom,
            IsConfirm: values.Contacted !== false,
            Id: String(summaryNoteType.Custom.value),
            DisplayName: summaryNoteType.Custom.name,
        });

        dispatch(setEngagementDraft(data));
        handleOnCloseModal();
        onChangeNote('');
    };

    const handleOnIsConfirmCheckbox = (checked: boolean, noteIndex: number, values: DocumentEngagement) => {
        let data = copyObject(values);
        data.SummaryNotes = data.SummaryNotes || [];
        if (noteIndex !== -1) {
            data.SummaryNotes[noteIndex].IsConfirm = checked;
            dispatch(setEngagementDraft(data));
        }
    };

    const handleOnToggleAllIsConfirmCheckbox = (checked: boolean, values: DocumentEngagement) => {
        let data = copyObject(values);
        data.SummaryNotes = data.SummaryNotes || [];
        data.SummaryNotes.forEach((item) => {
            item.IsConfirm = checked;
        });
        dispatch(setEngagementDraft(data));
    };

    const handleOnCloseModal = () => dispatch(toggleShowSummaryNoteModal(false));

    const handleOnShowModal = () => {
        dispatch(toggleShowSummaryNoteModal(true));
        onChangeNote('');
    };

    const isERVisitType = (reasonForVisitId) => {
        if (Boolean(reasonForVisitId) && (String(reasonForVisitId) === '56695e83ad78ee0e48b04a66' || String(reasonForVisitId) === '56695e83ad78ee0e48b04a64')) {
            return true;
        } else {
            return false;
        }
    };

    const renderAddSummaryNote = (values) => {
        return (
            <Modal open={showSummaryNoteModal} onClose={handleOnCloseModal}>
                <Box sx={classes.modalPopupForm}>
                    <div className="mui-modal-header">
                        <Typography variant="h6" component="label">
                            Summary Notes
                        </Typography>
                    </div>
                    <div className="mui-modal-body">
                        <Box sx={classes.richTextBox}>
                            <RichTextEditor editor={newNoteEditor} id="summary-note-input">
                                <RichTextEditor.Toolbar>
                                    <RichTextEditor.ControlsGroup>
                                        <RichTextEditor.Bold />
                                        <RichTextEditor.Italic />
                                        <RichTextEditor.Underline />
                                    </RichTextEditor.ControlsGroup>
                                </RichTextEditor.Toolbar>
                                <RichTextEditor.Content />
                            </RichTextEditor>
                        </Box>
                    </div>
                    <div className="mui-modal-footer">
                        <Button
                            variant="contained"
                            onClick={() => handleOnAddSummaryNote(values)}
                            disabled={isSaving}
                            endIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
                        >
                            Save
                        </Button>
                        <Button size="small" type="button" onClick={handleOnCloseModal} variant="outlined" disabled={isSaving}>
                            Cancel
                        </Button>
                    </div>
                </Box>
            </Modal>
        );
    };

    return (
        <Formik sx={classes.form} initialValues={engagementDraft} enableReinitialize={true} onSubmit={handleSubmit} validationSchema={summaryNoteFormSchema}>
            {({ values, setFieldValue, errors, handleChange, handleBlur, validateForm, submitCount }: FormikProps<DocumentEngagement>) => {
                const summaryNotes = values.SummaryNotes || [];
                const summaryNotesWithFCodesOrTCodes = summaryNotes.filter(
                    (s) => s.Zcode?.toLowerCase()?.startsWith('t') || s.Zcode?.toLowerCase()?.startsWith('f')
                );
                const summaryNotesWithoutFCodesOrTCodes =
                    isBHDiagnostic || hasCompletedAllBHDiagnosticAssessments
                        ? summaryNotes.filter((s) => !s.Zcode?.toLowerCase()?.startsWith('t') && !s.Zcode?.toLowerCase()?.startsWith('f'))
                        : summaryNotes;
                return (
                    <Form>
                        <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 4 }}>
                            <Typography variant="h6">Summary Notes</Typography>
                            <Tooltip title="Summary Notes Help Document">
                                <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                            </Tooltip>
                        </Stack>
                        <FCodeModal values={values} />
                        <ConfirmationDialog
                            open={showConfirmationDialog}
                            onClose={() => setShowConfirmationDialog(false)}
                            onConfirm={() => {
                                handleSubmitAssessment(values);
                                setShowConfirmationDialog(false);
                            }}
                        />
                        <Grid container direction="column" spacing={2} className="content-panel" sx={{ mt: 2 }}>
                            <Grid className="content-form">
                                {isFetching || !isPageLoaded ? (
                                    <Box display="flex" justifyContent="center" className="loader-container">
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Grid container spacing={2}>
                                        <Grid size={3}>
                                            <FormControl
                                                fullWidth
                                                size="small"
                                                error={
                                                    (Boolean(errors?.MeansOfEngagement) ||
                                                        String(values.MeansOfEngagement) === '0' ||
                                                        parseInt(String(values.MeansOfEngagement)) === 0 ||
                                                        !Boolean(values.MeansOfEngagement) ||
                                                        values.MeansOfEngagement === null) &&
                                                    Boolean(submitCount)
                                                }
                                            >
                                                <InputLabel id="means-of-enga-type-select" required>
                                                    Means of Engagement
                                                </InputLabel>
                                                <Select
                                                    labelId="means-of-enga-type-select"
                                                    id="means-of-enga-type-select"
                                                    value={
                                                        Boolean(values.MeansOfEngagement) &&
                                                        String(values.MeansOfEngagement) !== '0' &&
                                                        parseInt(String(values.MeansOfEngagement)) !== 0
                                                            ? values.MeansOfEngagement
                                                            : null
                                                    }
                                                    label="Means of Engagement*"
                                                    name="MeansOfEngagement"
                                                    onChange={handleChange}
                                                >
                                                    {meansOfEngagements.map((type) => (
                                                        <MenuItem key={type.Id} value={String(type.Id)}>
                                                            {type.Name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {Boolean(submitCount) && (
                                                    <FormHelperText>
                                                        {String(values.MeansOfEngagement) === '0' ||
                                                        parseInt(String(values.MeansOfEngagement)) === 0 ||
                                                        !Boolean(values.MeansOfEngagement) ||
                                                        values.MeansOfEngagement === null
                                                            ? 'Select means of engagement.'
                                                            : errors?.MeansOfEngagement}
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                            {String(values.MeansOfEngagement) === String(MeansOfEngagementEnum.Phone) ? (
                                                <FormControl fullWidth size="small" sx={{ marginTop: 2 }}>
                                                    <InputLabel id="contact-result-type-select">Contact Result</InputLabel>
                                                    <Select
                                                        labelId="contact-result-type-select"
                                                        id="contact-result-type-select"
                                                        value={Boolean(values.ContactResult) ? values.ContactResult : ''}
                                                        label="Contact Result"
                                                        name="ContactResult"
                                                        onChange={handleChange}
                                                    >
                                                        <MenuItem value="">Select Contact Result</MenuItem>
                                                        {phoneOptions.map((option) => (
                                                            <MenuItem key={option.Name} value={option.Name}>
                                                                {option.Name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            ) : null}
                                        </Grid>
                                        <Grid size={3}>
                                            <FormControl
                                                fullWidth
                                                size="small"
                                                error={(Boolean(errors?.ReasonForVisit) || Boolean(errors?.ReasonForVisit?.Id)) && Boolean(submitCount)}
                                            >
                                                <InputLabel id="reason-type-select" required>
                                                    Reason for Engagement
                                                </InputLabel>
                                                <Select
                                                    labelId="reason-type-select"
                                                    id="reason-type-select"
                                                    value={Boolean(values.ReasonForVisit?.Id) ? values.ReasonForVisit.Id : ''}
                                                    label="Reason for Engagement*"
                                                    name="ReasonForVisit.Id"
                                                    onChange={(e) => changeReasonForVisit(e.target.value, values, setFieldValue)}
                                                >
                                                    {reasonForVisits.map((type) => (
                                                        <MenuItem key={type.Id} value={String(type.Id)}>
                                                            {type.Name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {Boolean(submitCount) && (
                                                    <FormHelperText>
                                                        {(Boolean(errors?.ReasonForVisit) || Boolean(errors?.ReasonForVisit?.Id)) && Boolean(submitCount)
                                                            ? 'Select reason.'
                                                            : ''}
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                            {isERVisitType(values.ReasonForVisit?.Id) ? (
                                                <FormControl fullWidth size="small" sx={{ marginTop: 2 }}>
                                                    <InputLabel id="avoided-er-type-select">Avoided ER</InputLabel>
                                                    <Select
                                                        labelId="avoided-er-type-select"
                                                        displayEmpty
                                                        id="avoided-er-type-select"
                                                        value={String(values.AvoidedER)}
                                                        label="Avoided ER"
                                                        name="AvoidedER"
                                                        onChange={(e) =>
                                                            setFieldValue(e.target.name, Boolean(e.target.value) ? e.target.value === 'true' : e.target.value)
                                                        }
                                                    >
                                                        <MenuItem value={String(true)}>Yes</MenuItem>
                                                        <MenuItem value={String(false)}>No</MenuItem>
                                                        <MenuItem value="">Not applicable</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            ) : null}
                                        </Grid>
                                        <Grid size={3}>
                                            <Tooltip
                                                placement="top"
                                                title={`The engagement date must be set on the previous menu item "Set Appointment Info" by selecting an appointment. If there is no appointment for your assessment, one can be made from the previous tab as well.`}
                                            >
                                                <div>
                                                    <DesktopDatePicker
                                                        disabled
                                                        label="Engagement Date"
                                                        value={values.VisitedTime ? new Date(values.VisitedTime) : null}
                                                        format="MM/dd/yyyy"
                                                        minDate={new Date('2017-01-01')}
                                                        onChange={(date) => {
                                                            if (!date) {
                                                                setFieldValue('AdmissionDate', null);
                                                                return;
                                                            }
                                                            const d = toDate(date);
                                                            if (d && isValid(d)) {
                                                                setFieldValue('AdmissionDate', d.toISOString());
                                                            }
                                                        }}
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                required: true,
                                                                size: 'small',
                                                                error: Boolean(errors?.VisitedTime) && Boolean(submitCount),
                                                                helperText: Boolean(submitCount) ? errors?.VisitedTime : '',
                                                            },
                                                        }}
                                                    />
                                                </div>
                                            </Tooltip>
                                        </Grid>
                                        <Grid size={3}>
                                            <FormControl fullWidth size="small" error={Boolean(errors?.Contacted) && Boolean(submitCount)}>
                                                <InputLabel id="contact-type-select" required>
                                                    Contacted
                                                </InputLabel>
                                                <Select
                                                    labelId="contact-type-select"
                                                    id="contact-type-select"
                                                    value={values.Contacted}
                                                    label="Contacted*"
                                                    name="Contacted"
                                                    onChange={(e) => {
                                                        dispatch(setEngagementContacted(e.target.value === 'true', values));
                                                    }}
                                                >
                                                    <MenuItem value={'true'}>Yes</MenuItem>
                                                    <MenuItem value={'false'}>No</MenuItem>
                                                </Select>
                                                {Boolean(submitCount) && <FormHelperText>{errors?.Contacted}</FormHelperText>}
                                            </FormControl>
                                        </Grid>
                                        {isBHDiagnostic || hasCompletedAllBHDiagnosticAssessments ? (
                                            <Grid size={12} sx={{ marginY: 2 }}>
                                                <Stack spacing={2}>
                                                    <Stack direction={'row'} justifyContent="space-between" alignItems={'center'} sx={{ marginY: 2 }}>
                                                        <Typography variant="h6" component="label">
                                                            BH Diagnostic Results
                                                        </Typography>
                                                        {hasCompletedAllBHDiagnosticAssessments ? (
                                                            <Tooltip title="All diagnostic assessments are completed.">
                                                                <Stack direction={'row'} spacing={1}>
                                                                    <Typography>
                                                                        Status: <b>Complete</b>
                                                                    </Typography>
                                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                                </Stack>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title="Some diagnostic assessments are incomplete.">
                                                                <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                                                    <Typography>
                                                                        Status: <b>Incomplete</b>
                                                                    </Typography>
                                                                    <DangerousIcon color="error" fontSize="small" />
                                                                </Stack>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                    <hr className="hr-lite" />
                                                    <Paper elevation={3} sx={{ padding: 2, width: '100%' }}>
                                                        <Stack spacing={3}>
                                                            <Grid container spacing={2}>
                                                                {bhDiagnosticAssessmentsList.map((item, index) => {
                                                                    const bhDiagnosticAssessment = assessmentMenuList.find(
                                                                        (assessment) => assessment.Name === item
                                                                    );
                                                                    if (!bhDiagnosticAssessment) {
                                                                        return null;
                                                                    }
                                                                    return (
                                                                        <Grid key={item + index}>
                                                                            <Stack direction={'row'} spacing={0}>
                                                                                {bhDiagnosticAssessment?.IsCompleted ? (
                                                                                    <Tooltip title="Complete">
                                                                                        <CheckCircleIcon color="success" fontSize="small" />
                                                                                    </Tooltip>
                                                                                ) : (
                                                                                    <Tooltip title="Incomplete">
                                                                                        <DangerousIcon color="error" fontSize="small" />
                                                                                    </Tooltip>
                                                                                )}
                                                                                <Typography variant="body1" component="label">
                                                                                    {bhDiagnosticAssessment?.Name ?? ''}
                                                                                </Typography>
                                                                            </Stack>
                                                                        </Grid>
                                                                    );
                                                                })}
                                                            </Grid>
                                                            <hr className="hr-lite" />
                                                            <Stack
                                                                id="diagnostic-results"
                                                                sx={{ scrollMarginTop: '200px' }}
                                                                spacing={2}
                                                                direction={{ xs: 'column', md: 'row' }}
                                                                alignItems={{ xs: 'center', md: 'flex-start' }}
                                                                justifyContent={{ xs: 'flex-start', md: 'space-between' }}
                                                            >
                                                                <FormControl sx={{ maxWidth: 350 }} fullWidth>
                                                                    <InputLabel size="small" id="cpt-code-select">
                                                                        CPT Code
                                                                    </InputLabel>
                                                                    <Select
                                                                        labelId="cpt-code-select"
                                                                        disabled={Boolean(!hasCompletedAllBHDiagnosticAssessments)}
                                                                        size="small"
                                                                        label="CPT Code"
                                                                        error={cptCodeError}
                                                                        value={values.DiagnosticStatus?.CPTcode?.Id || ''}
                                                                        onChange={(e) => {
                                                                            setCptCodeError(false);
                                                                            setFieldValue('DiagnosticStatus.CPTcode.Id', e.target.value);
                                                                            const selectedCPTCode = bhCPTCodeGroup.find((cpt) => cpt.Id === e.target.value);
                                                                            if (selectedCPTCode) {
                                                                                setFieldValue('DiagnosticStatus.CPTcode', selectedCPTCode);
                                                                            }
                                                                        }}
                                                                        name="CPTCode"
                                                                    >
                                                                        {bhCPTCodeGroup.map((cptCodeGroup) => (
                                                                            <MenuItem
                                                                                disabled={checkCodeIsDisabled(cptCodeGroup)}
                                                                                key={cptCodeGroup.Id}
                                                                                value={cptCodeGroup.Id}
                                                                            >
                                                                                {cptCodeGroup.Code} - {cptCodeGroup.Description}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                    <FormHelperText>
                                                                        * All BH Diagnostic assessments must be complete before selecting CPT Code
                                                                    </FormHelperText>
                                                                </FormControl>
                                                                <Stack spacing={2} direction="row" alignItems="center">
                                                                    <Button
                                                                        sx={{ minWidth: 120 }}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        onClick={() => window.open(`${DSM5}`, '_blank')}
                                                                    >
                                                                        Open DSM 5
                                                                    </Button>
                                                                    <Button
                                                                        sx={{ minWidth: 120 }}
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="success"
                                                                        onClick={handleOpenAddFcodeModal}
                                                                    >
                                                                        Add F code
                                                                    </Button>
                                                                </Stack>
                                                            </Stack>

                                                            <TableContainer sx={classes.tableContainer}>
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell width="55%">Diagnosis Codes</TableCell>
                                                                            <TableCell width="20%">Source</TableCell>
                                                                            <TableCell align="center">
                                                                                <FormControlLabel
                                                                                    sx={classes.customCheckbox}
                                                                                    value="start"
                                                                                    control={
                                                                                        <Checkbox
                                                                                            size="small"
                                                                                            disabled={values.Contacted === false}
                                                                                            onChange={(e) =>
                                                                                                handleOnToggleAllIsConfirmCheckbox(
                                                                                                    e.currentTarget.checked,
                                                                                                    values
                                                                                                )
                                                                                            }
                                                                                            checked={summaryNotes && summaryNotes.every((s) => s.IsConfirm)}
                                                                                            indeterminate={
                                                                                                summaryNotes &&
                                                                                                !summaryNotes.every((s) => s.IsConfirm) &&
                                                                                                summaryNotes.some((s) => s.IsConfirm)
                                                                                            }
                                                                                        />
                                                                                    }
                                                                                    label="Add to Document Engagement"
                                                                                    labelPlacement="start"
                                                                                />
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {!summaryNotesWithFCodesOrTCodes || !summaryNotesWithFCodesOrTCodes.length ? (
                                                                            <TableRow hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                                                <TableCell colSpan={3} sx={{ paddingY: 3 }}>
                                                                                    <span>No notes to display.</span>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ) : (
                                                                            summaryNotesWithFCodesOrTCodes.map((note, index) => (
                                                                                <TableRow
                                                                                    key={`summary-note-${note.Id}-${index}`}
                                                                                    hover
                                                                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                                                >
                                                                                    <TableCell>
                                                                                        {summaryNoteType.ConsentForm.value === note.Type ? (
                                                                                            <span>{note.Note}</span>
                                                                                        ) : (
                                                                                            <span>{`${note.Note}`}</span>
                                                                                        )}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <span>
                                                                                            {summaryNoteTypeList.find((s) => s.value === note.Type)?.name ||
                                                                                                '-'}
                                                                                        </span>
                                                                                    </TableCell>
                                                                                    <TableCell align="center">
                                                                                        <Checkbox
                                                                                            size="small"
                                                                                            disabled={isSummaryNoteConfirmationDisabled(values.Contacted, note)}
                                                                                            checked={note.IsConfirm}
                                                                                            onChange={(e) =>
                                                                                                handleOnIsConfirmCheckbox(
                                                                                                    e.currentTarget.checked,
                                                                                                    summaryNotes.indexOf(note),
                                                                                                    values
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))
                                                                        )}
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                        </Stack>
                                                    </Paper>
                                                </Stack>
                                            </Grid>
                                        ) : null}
                                        <Grid size={12}>
                                            <Stack direction={'row'} justifyContent="space-between">
                                                <Typography variant="h6" component="label">
                                                    Summary Notes
                                                </Typography>
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleOnShowModal}
                                                    style={{ textTransform: 'none' }}
                                                >
                                                    Add Summary Note
                                                </Button>
                                            </Stack>
                                            <TableContainer sx={classes.tableContainer}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell width="55%">Notes</TableCell>
                                                            <TableCell width="20%">Source</TableCell>
                                                            <TableCell align="center">
                                                                <FormControlLabel
                                                                    sx={classes.customCheckbox}
                                                                    value="start"
                                                                    control={
                                                                        <Checkbox
                                                                            size="small"
                                                                            disabled={values.Contacted === false}
                                                                            onChange={(e) =>
                                                                                handleOnToggleAllIsConfirmCheckbox(e.currentTarget.checked, values)
                                                                            }
                                                                            checked={summaryNotes && summaryNotes.every((s) => s.IsConfirm)}
                                                                            indeterminate={
                                                                                summaryNotes &&
                                                                                !summaryNotes.every((s) => s.IsConfirm) &&
                                                                                summaryNotes.some((s) => s.IsConfirm)
                                                                            }
                                                                        />
                                                                    }
                                                                    label="Add to Document Engagement"
                                                                    labelPlacement="start"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {!summaryNotesWithoutFCodesOrTCodes || !summaryNotesWithoutFCodesOrTCodes.length ? (
                                                            <TableRow hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                                <TableCell colSpan={3} sx={{ paddingY: 3 }}>
                                                                    <span>No notes to display.</span>
                                                                </TableCell>
                                                            </TableRow>
                                                        ) : (
                                                            summaryNotesWithoutFCodesOrTCodes.map((note, index) => (
                                                                <TableRow
                                                                    key={`summary-note-${note.Id}-${index}`}
                                                                    hover
                                                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                                >
                                                                    <TableCell>
                                                                        {summaryNoteType.ConsentForm.value === note.Type ? (
                                                                            <span>{note.Note}</span>
                                                                        ) : (
                                                                            <span dangerouslySetInnerHTML={{ __html: note.Note }} />
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <span>{summaryNoteTypeList.find((s) => s.value === note.Type)?.name || '-'}</span>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Checkbox
                                                                            size="small"
                                                                            disabled={isSummaryNoteConfirmationDisabled(values.Contacted, note)}
                                                                            checked={note.IsConfirm}
                                                                            onChange={(e) =>
                                                                                handleOnIsConfirmCheckbox(e.currentTarget.checked, summaryNotes.indexOf(note), values)
                                                                            }
                                                                        />
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            {renderAddSummaryNote(values)}
                                        </Grid>

                                        <Grid size={12} sx={{ marginTop: 4, marginBottom: 2 }}>
                                            <Stack spacing={2}>
                                                <Typography variant="body1" component="label" fontStyle={'italic'}>
                                                    Member has been offered access to multiple supports and resources including: behavioral health therapy,
                                                    psychiatric care, primary care, care coordination, social determinants of health resources (housing, food,
                                                    transportation), and peer support services.
                                                </Typography>
                                                <RadioGroup
                                                    row
                                                    name="StatementOfCare"
                                                    sx={{ alignSelf: 'flex-end' }}
                                                    value={values.StatementOfCare || ''}
                                                    onChange={handleChange}
                                                >
                                                    <Stack spacing={0}>
                                                        {statementOfCareOptions.map((option, index) => (
                                                            <FormControlLabel
                                                                key={`statement-of-care-${index}`}
                                                                value={option}
                                                                control={<Radio />}
                                                                label={option}
                                                            />
                                                        ))}
                                                    </Stack>
                                                </RadioGroup>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                            {isFetching || !isPageLoaded ? null : (
                                <Grid justifyContent="flex-end" className="content-footer">
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ mr: 3 }}
                                        disabled={isSaving}
                                        onClick={() => dispatch(gotoPreviousMenu())}
                                        startIcon={<ArrowBackIosIcon />}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isSaving || (isBHDiagnostic && !hasCompletedAllBHDiagnosticAssessments)}
                                        endIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIosIcon />}
                                    >
                                        Next
                                    </Button>
                                </Grid>
                            )}
                            <AlertDialog
                                open={showAlert}
                                title="Reason For Visit"
                                cancelText="Go Back"
                                okText="Confirm"
                                onConfirm={() => handleOnConfirm(values, setFieldValue)}
                                onClose={() => setShowAlert(false)}
                                showBottomCenter={true}
                                message="Are you sure you want to change the Reason for Engagement?"
                            />
                        </Grid>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default SummaryNotes;
